import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Search, 
  MapPin, 
  Trophy, 
  Calendar, 
  Users, 
  Loader2,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelGuidesPage = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/travel-guides`);
        setGuides(res.data.guides || []);
      } catch (err) {
        console.error('Failed to fetch travel guides:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = search
    ? guides.filter(g => g.state_name.toLowerCase().includes(search.toLowerCase()) || g.state_code.toLowerCase().includes(search.toLowerCase()))
    : guides;

  const withData = filtered.filter(g => g.has_data);
  const withoutData = filtered.filter(g => !g.has_data);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Minority Golfers Weekends</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="travel-guides-title">
            Travel Guides
          </h1>
          <p className="text-cream/70 font-body max-w-2xl">
            Plan your next golf trip. Find minority-owned courses, local leagues, upcoming events, and pro instructors in all 50 states.
          </p>
        </div>
      </div>

      <div className="container-padding">
        {/* Search */}
        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search states..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white"
            data-testid="travel-guides-search"
          />
        </div>

        {/* States with data */}
        {withData.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display font-bold text-xl text-deep-green mb-4">
              States with Golf Directory Listings ({withData.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {withData.map(guide => (
                <Link key={guide.state_code} to={`/travel-guides/${guide.state_code}`}>
                  <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all group cursor-pointer h-full" data-testid="travel-guide-card">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-fairway flex items-center justify-center shrink-0">
                            <span className="text-gold font-bold text-sm">{guide.state_code}</span>
                          </div>
                          <div>
                            <h3 className="font-display font-bold text-deep-green group-hover:text-gold transition-colors">{guide.state_name}</h3>
                            <p className="text-muted-foreground text-xs">{guide.total_listings} listings</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gold transition-colors" />
                      </div>
                      <div className="flex gap-3 text-xs">
                        {guide.courses > 0 && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-3 h-3 text-gold" /> {guide.courses} courses
                          </span>
                        )}
                        {guide.events > 0 && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-3 h-3 text-gold" /> {guide.events} events
                          </span>
                        )}
                        {guide.leagues > 0 && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-3 h-3 text-gold" /> {guide.leagues} leagues
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* States without data */}
        {withoutData.length > 0 && (
          <div>
            <h2 className="font-display font-bold text-xl text-deep-green mb-4">
              Coming Soon ({withoutData.length} states)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {withoutData.map(guide => (
                <div key={guide.state_code} className="bg-white/60 rounded-sm p-3 text-center opacity-60" data-testid="travel-guide-coming-soon">
                  <p className="font-display font-bold text-sm text-deep-green">{guide.state_code}</p>
                  <p className="text-muted-foreground text-xs">{guide.state_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelGuidesPage;
